import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { apiClient as LitLingoClient } from 'client';
import { providerS3Keys } from 'constants/platform';
import { sha256 } from 'js-sha256';
import {
  customerArtifactItemDone,
  customerArtifactItemFailed,
  customerArtifactItemProgress,
  customerArtifactItemUploadStart,
  customerArtifactPresignFailed,
  customerArtifactPresignSucceeded,
  customerArtifactUploadBatchFinished,
  uploadCustomerArtifacts,
  uploadFileS3,
  uploadFileS3Failure,
  uploadFileS3Fulfill,
  uploadFileS3Progress,
  uploadFileS3Success,
  uploadFileWithS3URL,
} from 'reducers/fileUploads';
import type { EventChannel } from 'redux-saga';
import { END, buffers, eventChannel } from 'redux-saga';
import { all, call, put, take, takeLatest } from 'redux-saga/effects';
import type { API, ErrorObject, SagaReturn } from 'types';

const buildFilename = (file: File, provider: 'o365' | 'zendesk'): string => {
  const parts = file.name.split('.');
  const extension = parts[parts.length - 1];

  const timestamp = new Date().toISOString();

  return `${providerS3Keys[provider]}-${timestamp}.${extension}`;
};

const contentTypeFor = (file: File): string =>
  file.type && file.type.length > 0 ? file.type : 'application/octet-stream';

type UploadInfo = {
  progress?: number;
  success?: true;
  error?: ErrorObject;
};

const createChannel = (url: string, file: File): EventChannel<UploadInfo> =>
  eventChannel<UploadInfo>((emmiter) => {
    const source = axios.CancelToken.source();
    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': contentTypeFor(file),
      },
      cancelToken: source.token,
      onUploadProgress: (progress) => {
        const percent = progress.total ? Math.round((progress.loaded * 100) / progress.total) : 0;
        emmiter({ progress: percent });
      },
    };

    axios
      .put(url, file, config)
      .then(() => {
        emmiter({ success: true });
        emmiter(END);
      })
      .catch((reason) => {
        emmiter({ error: reason });
        emmiter(END);
      });

    return (): void => {
      source.cancel();
    };
  }, buffers.sliding(2));

type UploadAction = ReturnType<typeof uploadFileS3>;
function* uploadFileS3Saga(action: UploadAction): SagaReturn {
  try {
    const filename = buildFilename(action.payload.file, action.payload.provider);

    const response = (yield call([LitLingoClient.resources.customers.extras, 'uploadURL'], {
      params: { file_name: filename },
    })) as API.Response<API.Customers.UploadURL>;

    if (response.error != null) {
      yield put(uploadFileS3Failure({ error: response.error }));
    } else {
      const channel = createChannel(response.data.payload, action.payload.file);

      while (true) {
        const { progress, error, success } = (yield take(channel)) as UploadInfo;
        if (error != null) {
          yield put(uploadFileS3Failure({ error }));
          break;
        }

        if (success) {
          yield put(uploadFileS3Success());
          break;
        }

        if (progress != null) {
          yield put(uploadFileS3Progress({ progress }));
        }
      }
    }
  } catch (error) {
    const errorObject = error as ErrorObject;
    yield put(uploadFileS3Failure({ error: errorObject }));
  } finally {
    yield put(uploadFileS3Fulfill());
  }
}

function* uploadFileWithS3URLSaga(action: UploadAction): SagaReturn {
  try {
    const { file, fileName, uploadURL } = action.payload;

    if (!fileName || !uploadURL) return;

    const channel = createChannel(uploadURL, file);

    while (true) {
      const { progress, error, success } = (yield take(channel)) as UploadInfo;
      if (error != null) {
        yield put(uploadFileS3Failure({ error }));
        break;
      }

      if (success) {
        yield put(uploadFileS3Success());
        break;
      }

      if (progress != null) {
        yield put(uploadFileS3Progress({ progress }));
      }
    }
  } catch (error) {
    const errorObject = error as ErrorObject;
    yield put(uploadFileS3Failure({ error: errorObject }));
  } finally {
    yield put(uploadFileS3Fulfill());
  }
}

const MULTIPART_CHUNK_SIZE = 50 * 1024 * 1024;

const normalizeEtag = (raw: string): string => raw.replace(/^"+|"+$/g, '');

export const hashFileIncremental = async (
  file: File
): Promise<{ fileHash: string; contentType: string }> => {
  const hasher = sha256.create();
  for (let offset = 0; offset < file.size; offset += MULTIPART_CHUNK_SIZE) {
    const end = Math.min(file.size, offset + MULTIPART_CHUNK_SIZE);
    const chunk = file.slice(offset, end);
    // eslint-disable-next-line no-await-in-loop
    const chunkBuffer = await chunk.arrayBuffer();
    hasher.update(new Uint8Array(chunkBuffer));
  }
  return {
    fileHash: hasher.hex(),
    contentType: contentTypeFor(file),
  };
};

function* uploadCustomerArtifactsSaga(
  action: ReturnType<typeof uploadCustomerArtifacts>
): SagaReturn {
  const { files } = action.payload;
  if (files.length === 0) {
    return;
  }

  try {
    const prepared: {
      file_name: string;
      file_hash: string;
      contentType: string;
    }[] = [];

    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const one = (yield call(hashFileIncremental, file)) as {
        fileHash: string;
        contentType: string;
      };
      prepared.push({
        file_name: file.name,
        file_hash: one.fileHash,
        contentType: one.contentType,
      });
    }

    const response = (yield call(
      [LitLingoClient.resources.customers.extras, 'uploadMultipartStart'],
      {
        data: {
          files: prepared.map((p) => ({
            file_name: p.file_name,
            file_hash: p.file_hash,
            content_type: p.contentType,
          })),
        },
      }
    )) as API.Response<API.Customers.UploadMultipartStart>;

    if (response.error != null) {
      yield put(
        customerArtifactPresignFailed({
          message: response.error.message,
        })
      );
      return;
    }

    const rows = response.data.payload;
    if (rows.length !== files.length) {
      yield put(
        customerArtifactPresignFailed({
          message: 'Unexpected response from upload-multipart/start',
        })
      );
      return;
    }

    yield put(customerArtifactPresignSucceeded());

    for (let i = 0; i < files.length; i += 1) {
      const row = rows[i];
      const file = files[i];
      const totalParts = Math.ceil(file.size / MULTIPART_CHUNK_SIZE);
      let uploadedBytes = 0;
      const completedParts: { part_number: number; etag: string }[] = [];

      yield put(customerArtifactItemUploadStart({ index: i, key: row.key }));
      let failedMessage: string | null = null;

      for (let partIdx = 0; partIdx < totalParts; partIdx += 1) {
        const partNumber = partIdx + 1;
        const start = partIdx * MULTIPART_CHUNK_SIZE;
        const end = Math.min(file.size, start + MULTIPART_CHUNK_SIZE);
        const chunk = file.slice(start, end);

        const partUrlResponse = (yield call(
          [LitLingoClient.resources.customers.extras, 'uploadMultipartPartURL'],
          {
            data: {
              key: row.key,
              upload_id: row.upload_id,
              part_number: partNumber,
            },
          }
        )) as API.Response<API.Customers.UploadMultipartPartUrl>;
        if (partUrlResponse.error != null) {
          failedMessage = partUrlResponse.error.message || 'Could not get multipart part URL';
          break;
        }

        try {
          const putResponse = (yield call(
            axios.put,
            partUrlResponse.data.payload.upload_url,
            chunk,
            {
              headers: { 'Content-Type': contentTypeFor(file) },
            }
          )) as { headers?: Record<string, string> };
          const etagHeader = putResponse.headers?.etag || putResponse.headers?.ETag;
          if (typeof etagHeader !== 'string' || etagHeader.trim() === '') {
            failedMessage = 'Missing ETag from multipart part upload';
            break;
          }
          completedParts.push({ part_number: partNumber, etag: normalizeEtag(etagHeader) });
          uploadedBytes += chunk.size;
          const progress = file.size > 0 ? Math.round((uploadedBytes * 100) / file.size) : 100;
          yield put(customerArtifactItemProgress({ index: i, progress }));
        } catch (error) {
          failedMessage =
            (axios.isAxiosError(error) ? error.message : undefined) || 'Upload failed';
          break;
        }
      }

      if (failedMessage != null) {
        yield call([LitLingoClient.resources.customers.extras, 'uploadMultipartAbort'], {
          data: {
            key: row.key,
            upload_id: row.upload_id,
          },
        });
        yield put(customerArtifactItemFailed({ index: i, message: failedMessage }));
      } else {
        const completeResponse = (yield call(
          [LitLingoClient.resources.customers.extras, 'uploadMultipartComplete'],
          {
            data: {
              key: row.key,
              upload_id: row.upload_id,
              parts: completedParts,
            },
          }
        )) as API.Response<{ ok: boolean }>;
        if (completeResponse.error != null) {
          yield put(
            customerArtifactItemFailed({
              index: i,
              message: completeResponse.error.message || 'Could not complete multipart upload',
            })
          );
        } else {
          yield put(customerArtifactItemDone({ index: i, key: row.key }));
        }
      }
    }

    yield put(customerArtifactUploadBatchFinished());
  } catch (error) {
    const errorObject = error as ErrorObject;
    yield put(
      customerArtifactPresignFailed({
        message:
          typeof errorObject === 'object' && errorObject != null && 'message' in errorObject
            ? String((errorObject as { message?: string }).message)
            : 'Upload failed',
      })
    );
  }
}

function* fileUploadsSaga(): SagaReturn {
  yield all([
    takeLatest(uploadFileS3, uploadFileS3Saga),
    takeLatest(uploadFileWithS3URL, uploadFileWithS3URLSaga),
    takeLatest(uploadCustomerArtifacts, uploadCustomerArtifactsSaga),
  ]);
}

export default fileUploadsSaga;
